const RESTORE_DELAY_MS = 100;
const FALLBACK_SWITCH_DELAY_MS = 1500;

let pendingReapply = null;

export const getGoogleTranslateTargetLang = () => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/([^/]+)\/([^;]+)/);
  if (!match) return null;
  const source = decodeURIComponent(match[1]);
  const target = decodeURIComponent(match[2]);
  return target && target !== source ? target : null;
};

const findTranslateCombo = () =>
  document.querySelector("#google-translate-element select") ||
  document.querySelector(".goog-te-combo");

const selectLanguage = (combo, lang) => {
  combo.value = lang;
  // eslint-disable-next-line no-undef
  combo.dispatchEvent(new Event("change"));
};

// Google's "Show original" button lives in its (CSS-hidden) banner iframe.
const findRestoreButton = () => {
  const frames = document.querySelectorAll("iframe");
  for (let i = 0; i < frames.length; i++) {
    try {
      const button = frames[i].contentDocument?.querySelector(
        'button[id$=".restore"]'
      );
      if (button) return button;
    } catch {
      console.warn(
        "Skipping cross-origin iframe while looking for Google Translate restore button."
      );
    }
  }
  return null;
};

const reapplyTranslation = (force) => {
  const combo = findTranslateCombo();
  const target = getGoogleTranslateTargetLang();
  if (!combo || !target) return false;

  if (combo.value !== target) {
    window.clearTimeout(pendingReapply);
    selectLanguage(combo, target);
    return true;
  }
  if (!force) return false;

  window.clearTimeout(pendingReapply);

  // Google ignores re-selecting the active language and does not translate
  // content React renders afterwards, so reset the page before re-applying.
  const restoreButton = findRestoreButton();
  if (restoreButton) {
    restoreButton.click();
    pendingReapply = setTimeout(
      () => selectLanguage(combo, target),
      RESTORE_DELAY_MS
    );
    return true;
  }

  const otherLanguage = Array.from(combo.options).find(
    (option) => option.value && option.value !== target
  );
  if (!otherLanguage) return false;
  selectLanguage(combo, otherLanguage.value);
  pendingReapply = setTimeout(
    () => selectLanguage(combo, target),
    FALLBACK_SWITCH_DELAY_MS
  );
  return true;
};

/**
 * Re-apply the participant's Google Translate language after dynamic survey
 * content changes (multipage next/back). Pinned surveys run inside an iframe
 * that does not host the visible widget; a hidden TranslateElement still
 * exposes the combo so this can re-trigger from the shared googtrans cookie.
 *
 * `force` resets and re-translates the whole page, which flashes the original
 * text; only use it when a full survey page has been replaced.
 */
const googleTranslateInit = (
  isEmbed,
  isGoogleTranslateEnabled,
  { force = false } = {}
) => {
  if (!isGoogleTranslateEnabled || (isEmbed && !findTranslateCombo())) {
    return false;
  }
  return reapplyTranslation(force);
};

export default googleTranslateInit;
